<template>
  <div
    v-if="visible"
    ref="previewRef"
    tabindex="-1"
    class="preview-layout"
    :style="{
      position: 'absolute',
      left: positionx + 'px',
      top: positiony + 'px',
      width: '546px',
      zIndex: 11
    }"
  >
    <!-- Header with icon -->
    <div class="px-6 py-3 flex justify-end items-center rounded-t-[10px]">
      <!-- Show expand icon in create mode (hide for clinic 150105) -->
      <img
        v-if="!isPreviewMode"
        data-testid="my-calendar.schedule-preview.expand-btn"
        class="cursor-pointer w-6 h-6" 
        src="@/assets/my-calendar/calendar/blue-expand.svg" 
        alt="expand" 
        @click="expand"
      />
      <!-- Show close icon in preview mode -->
      <img
        v-else-if="isPreviewMode"
        data-testid="my-calendar.schedule-preview.close-btn"
        class="cursor-pointer w-6 h-6" 
        src="@/assets/my-calendar/close.svg" 
        alt="close" 
        @click="$emit('close')"
      />
    </div>

    <!-- Show appointment details if in preview mode -->
    <div v-if="isPreviewMode" class="bg-white flex flex-col items-center py-3">
      <div class="flex flex-col px-[42px] w-full">
        <div class="flex flex-col gap-5 w-full">
          <!-- Title -->
          <div class="flex items-center justify-start">
            <p data-testid="my-calendar.schedule-preview.title-text" class="font-medium text-base text-[#333] tracking-[0.15px] leading-6">
              {{ previewTitle }}
            </p>
          </div>

          <!-- Details content -->
          <div class="flex flex-col gap-3 text-sm tracking-[0.15px] leading-6">
            <div class="flex gap-[10px] items-center">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden">
                Patient Name:
              </p>
              <p data-testid="my-calendar.schedule-preview.patient-name-text" class="font-medium text-[#333] overflow-hidden">
                {{ patientName }}
              </p>
            </div>

            <div v-if="providerNames.length > 0" class="flex gap-[10px] items-start w-full">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden leading-6">
                {{ providerLabel }}:
              </p>
              <p data-testid="my-calendar.schedule-preview.provider-names-text" class="font-medium text-[#333] flex-1 min-w-0 leading-6 break-words">
                {{ providerNames.join(', ') }}
              </p>
            </div>

            <div class="flex gap-[10px] items-center">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden">
                Time:
              </p>
              <p data-testid="my-calendar.schedule-preview.time-text" class="font-medium text-[#333] overflow-hidden">
                {{ formattedTime }}
              </p>
            </div>

            <div v-if="appointmentType && appointmentType !== 'N/A'" class="flex gap-[10px] items-center">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden">
                Event Type:
              </p>
              <p data-testid="my-calendar.schedule-preview.event-type-text" class="font-medium text-[#333] overflow-hidden">
                {{ appointmentType }}
              </p>
            </div>

            <div v-if="description && description !== 'N/A'" class="flex gap-[10px] items-start w-full">
              <p class="font-normal text-[#666] w-[120px] leading-6 whitespace-nowrap">
                Description:
              </p>
              <p data-testid="my-calendar.schedule-preview.description-text" class="font-medium text-[#333] flex-1 min-w-0 leading-6 break-words">
                {{ description }}
              </p>
            </div>

            <div v-if="location && location !== 'N/A'" class="flex gap-[10px] items-center">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden">
                Location:
              </p>
              <p data-testid="my-calendar.schedule-preview.location-text" class="font-medium text-[#333] flex-1 min-w-0 break-words">
                {{ location }}
              </p>
            </div>

            <div v-if="recurrenceLabel" class="flex gap-[10px] items-center">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden">
                Recurring:
              </p>
              <p data-testid="my-calendar.schedule-preview.recurrence-text" class="font-medium text-[#333] flex-1 min-w-0 break-words">
                {{ recurrenceLabel }}
              </p>
            </div>

            <div v-if="zoomLink" class="flex gap-[10px] items-start w-full">
              <p class="font-normal text-[#666] w-[120px] leading-6 whitespace-nowrap">
                Zoom Link:
              </p>
              <a
                v-if="isZoomLinkUrl"
                data-testid="my-calendar.schedule-preview.zoom-link"
                :href="zoomLink" 
                target="_blank"
                class="font-medium text-[#1e4670] underline flex-1 min-w-0 leading-6 cursor-pointer block break-words"
              >
                {{ zoomLink }}
              </a>
              <p
                v-else
                data-testid="my-calendar.schedule-preview.zoom-link-text"
                class="font-medium text-[#333] flex-1 min-w-0 leading-6 block break-words"
              >
                {{ zoomLink }}
              </p>
            </div>

            <div v-if="internalNotes" class="flex gap-[10px] items-start w-full" data-testid="my-calendar.schedule-preview.internal-notes-row">
              <p class="font-normal text-[#666] w-[120px] leading-6 whitespace-nowrap">
                Internal Notes:
              </p>
              <p data-testid="my-calendar.schedule-preview.internal-notes-text" class="internal-notes-value font-medium text-[#333] flex-1 min-w-0 leading-6 break-words whitespace-pre-line">
                {{ internalNotes }}
              </p>
            </div>

            <div v-if="reports && reports.length > 0" class="flex gap-[10px] items-start w-full">
              <p class="font-normal text-[#666] w-[120px] whitespace-nowrap overflow-hidden leading-6">
                Reports:
              </p>
              <div class="flex-1 min-w-0">
                <div class="flex flex-col gap-[4px]">
                  <!-- Show first row (2 reports) when collapsed, all rows when expanded -->
                  <template v-if="!reportsExpanded">
                    <div class="flex gap-[8px] items-start">
                      <div class="flex gap-[6px] items-center flex-1">
                        <span
                          v-for="report in reports.slice(0, 2)"
                          :key="report.id"
                          :data-testid="`my-calendar.schedule-preview.report-link.${report.id}`"
                          @click="handleReportClick(report)"
                          class="font-medium text-[#1e4670] underline text-sm leading-6 tracking-[0.15px] cursor-pointer whitespace-nowrap"
                        >
                          {{ report.name }}
                        </span>
                      </div>
                      <!-- Always reserve space for expand icon to keep consistent layout -->
                      <div class="flex items-center justify-center shrink-0 w-[20px] h-[20px] mt-[2px]">
                        <img
                          v-if="reports.length > 2"
                          data-testid="my-calendar.schedule-preview.expand-reports-btn"
                          src="@/assets/my-calendar/availability/arrow-left.svg"
                          alt="expand"
                          class="w-[16px] h-[16px] transition-transform duration-200 cursor-pointer"
                          :style="{ transform: 'rotate(-90deg)' }"
                          @click="toggleReportsDropdown"
                        />
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <!-- Show 2 reports per row (Classic and Modern pairs) -->
                    <div 
                      v-for="(reportPair, index) in groupedReports" 
                      :key="index"
                      class="flex gap-[8px] items-start"
                    >
                      <div class="flex gap-[6px] items-center flex-1">
                        <span
                          v-for="report in reportPair"
                          :key="report.id"
                          :data-testid="`my-calendar.schedule-preview.report-link.${report.id}`"
                          @click="handleReportClick(report)"
                          class="font-medium text-[#1e4670] underline text-sm leading-6 tracking-[0.15px] cursor-pointer whitespace-nowrap"
                        >
                          {{ report.name }}
                        </span>
                      </div>
                      <div
                        v-if="index === 0 && reports.length > 2"
                        data-testid="my-calendar.schedule-preview.collapse-reports-btn"
                        class="flex items-center justify-center shrink-0 w-[20px] h-[20px] cursor-pointer mt-[2px]"
                        @click="toggleReportsDropdown"
                      >
                        <img
                          src="@/assets/my-calendar/availability/arrow-left.svg"
                          alt="expand"
                          class="w-[16px] h-[16px] transition-transform duration-200"
                          :style="{ transform: 'rotate(90deg)' }"
                        />
                      </div>
                      <div v-else-if="index !== 0" class="shrink-0 w-[20px]"></div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Show tab panel if in create mode -->
    <div v-else class="py-3 px-5">
      <TabSwitchPanel v-model="activeTab"  @update:disabled="disableBtn = $event"/>
    </div>

    <!-- Footer with action buttons -->
    <div :class="['pb-[23px] px-[42px] flex flex-col items-start w-full', { 'mt-3': isPreviewMode }]">
      <div class="flex items-center justify-between w-full">
        <!-- Cancel button on left (only in schedule preview mode) -->
        <div v-if="!isPreviewMode" class="flex items-center">
          <ResponsiveButton
            data-testid="my-calendar.schedule-preview.cancel-btn"
            button_type="text"
            built_in_theme="dark"
            size="medium"
            display_name="Cancel"
            @click-button="$emit('close')"
          />
        </div>
        <div v-else></div>

        <!-- Action buttons on right -->
        <div class="flex gap-3 items-center justify-end">
          <!-- Create Encounter Notes button (only in preview mode, hidden for clinic 150105) -->
          <ResponsiveButton
            v-if="isPreviewMode && userInfo?.clinic_id !== 150105"
            data-testid="my-calendar.schedule-preview.create-encounter-notes-btn"
            button_type="outlined"
            built_in_theme="dark"
            size="medium"
            :display_name="creatingEncounterNote ? 'Creating Encounter Note...' : 'Create Encounter Notes'"
            :disabled="creatingEncounterNote || !patientId"
            @click-button="createEncounterNotes"
          >
            <!-- <template #prefix>
              <ArrowSmall v-if="creatingEncounterNote" class="encounter-note-loading-icon" />
            </template> -->
          </ResponsiveButton>
          
          <!-- Edit Detail button (only in preview mode) -->
          <ResponsiveButton
            v-if="isPreviewMode"
            data-testid="my-calendar.schedule-preview.edit-detail-btn"
            button_type="filled"
            built_in_theme="dark"
            size="medium"
            display_name="Edit Detail"
            @click-button="expand"
          />
          
          <!-- Create Appointment/Task button (only in create mode) -->
          <ResponsiveButton
            v-else-if="!isPreviewMode"
            data-testid="my-calendar.schedule-preview.submit-btn"
            button_type="filled"
            built_in_theme="dark"
            size="medium"
            :display_name="activeTab === 'appointment' ? 'Create Appointment' : 'Create Task'"
            :disabled="disableBtn"
            @click-button="createEvent"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted,watch } from 'vue';
import ArrowSmall from '@/NewLibrary/Loading/ArrowSmall.vue'
import TabSwitchPanel from './TabSwitchPanel.vue';
import { useStore } from 'vuex'
import moment from 'moment-timezone'
import { viewCompleteReportClassic, viewCompleteReportModern } from '@/api/report/report-methods'
import { createEncounterNoteApi } from '@/api/encounterNotes'
  
const store = useStore()

const props = defineProps({
  visible: Boolean,
  activeTab: {
    type: String,
    default: 'appointment',
  },
});
const emit = defineEmits(['close','expand','update:activeTab']);

const eventInfo = computed(() => store.state.calendar.currentEventInfo)
const userInfo = computed(() => store.state.login.userInfo)
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])
const lastCreatedEventResponse = computed(() => store.state.calendar.lastCreatedEventResponse)
const lastCreatedTaskResponse = computed(() => store.state.calendar.lastCreatedTaskResponse)
const lastUpdatedTaskResponse = computed(() => store.state.calendar.lastUpdatedTaskResponse)
const lastUpdatedEventResponse = computed(() => store.state.calendar.lastUpdatedEventResponse)

const activeTab = computed({
  get: () => props.activeTab,
  set: (val) => emit('update:activeTab', val)
})

const disableBtn = ref(true)
const reportsExpanded = ref(false)
const creatingEncounterNote = ref(false)

const toggleReportsDropdown = () => {
  reportsExpanded.value = !reportsExpanded.value
}

const handleReportClick = async (report) => {
  try {
    const accessionId = report.accessionId || eventInfo.value?.accessionId 
    console.log('Opening report:', report.name, 'for accession ID:', accessionId)
    
    if (report.type === 'classic' || report.name.includes('Classic')) {
      await viewCompleteReportClassic(accessionId)
    } else if (report.type === 'modern' || report.name.includes('Modern')) {
      await viewCompleteReportModern(accessionId)
    } else {
      console.warn('Unknown report type:', report)
    }
  } catch (error) {
    console.error('Failed to open report:', error)
  }
}

watch(disableBtn, (val) => {
  // console.log(val,'isDisabledBtn')
})

// Check if we're in preview mode (viewing existing event) or create mode
const isPreviewMode = computed(() => {
  return eventInfo.value?.mode === 'edit' || eventInfo.value?.event_id
})

// Computed properties for preview details
const previewTitle = computed(() => {
  if (!eventInfo.value) return ''
  return eventInfo.value.event_title || eventInfo.value.title || ''
})

const patientsList = computed(() =>
  store.state.calendar.rawPatientList.map(({ id, first_name, last_name, calendar_id }) => ({
    id: id,
    name: `${first_name} ${last_name}`,
    calendar_id: calendar_id,
  }))
)

const patientParticipant = computed(() => {
  const participants = eventInfo.value?.participants || []
  return participants.find(p => p.role === 'patient') || null
})

const providerParticipants = computed(() => {
  const participants = eventInfo.value?.participants || []
  return participants.filter(p => ['provider', 'clinicadmin'].includes(p.role))
})

const providerNames = computed(() =>
  providerParticipants.value
    .map(p => `${p.firstname || ''} ${p.lastname || ''}`.trim())
    .filter(Boolean)
)

const providerLabel = computed(() => {
  const isClinic150105 = userInfo.value?.clinic_id === 150105
  const base = isClinic150105 ? 'Clinician' : 'Provider'
  const suffix = providerNames.value.length > 1 ? 'Names' : 'Name'
  return `${base} ${suffix}`
})

const mappedPatient = computed(() => {
  const participantCalendarId = patientParticipant.value?.participant_calendar_id
  if (!participantCalendarId) return null

  return patientsList.value.find(
    p => String(p.calendar_id) === String(participantCalendarId)
  ) || null
})

const patientName = computed(() => {
  if (!eventInfo.value) return 'N/A'

  return mappedPatient.value?.name || 'N/A'
})

const patientId = computed(() => {
  if (!eventInfo.value) return ''

  if (eventInfo.value.patient_id) return String(eventInfo.value.patient_id)

  if (Array.isArray(eventInfo.value.patient_ids) && eventInfo.value.patient_ids.length > 0) {
    return String(eventInfo.value.patient_ids[0])
  }

  if (mappedPatient.value?.id) return String(mappedPatient.value.id)

  return ''
})

const formattedTime = computed(() => {
  if (!eventInfo.value) return ''
  const start = eventInfo.value.start_time || eventInfo.value.preview_start
  const end = eventInfo.value.end_time || eventInfo.value.preview_end
  if (!start || !end) return ''

  const dateStr = moment.utc(start).tz(calendarTimezone.value).format('dddd, MMMM D')
  const timeStr = `${moment.utc(start).tz(calendarTimezone.value).format('hh:mma')}-${moment.utc(end).tz(calendarTimezone.value).format('hh:mma')}`
  return `${dateStr}, ${timeStr}`
})

const appointmentType = computed(() => {
  if (!eventInfo.value) return 'N/A'
  return eventInfo.value.event_type || eventInfo.value.meetingType || 'N/A'
})

const location = computed(() => {
  if (!eventInfo.value) return 'N/A'
  return eventInfo.value.location || eventInfo.value.location_name || 'N/A'
})

const isRecurring = computed(() => {
  if (!eventInfo.value) return false
  return Boolean(eventInfo.value.rrule)
})

const recurrenceLabel = computed(() => {
  const raw = eventInfo.value?.rrule
  if (!raw) return ''

  const normalizedRaw = String(raw).trim()
  const dtstartMatch = normalizedRaw.match(/DTSTART[:=](\d{8})T(\d{6})Z/i)
  const rrulePart = normalizedRaw.toUpperCase().includes('RRULE:')
    ? normalizedRaw.split(/RRULE:/i).pop()
    : normalizedRaw
  const normalized = String(rrulePart).trim().toUpperCase()

  const byDayMatch = normalized.match(/BYDAY=([^;]+)/)
  const bySetPosMatch = normalized.match(/BYSETPOS=([^;]+)/)
  const byMonthMatch = normalized.match(/BYMONTH=([^;]+)/)
  const byMonthDayMatch = normalized.match(/BYMONTHDAY=([^;]+)/)

  const weekdayMap = {
    SU: 'Sunday',
    MO: 'Monday',
    TU: 'Tuesday',
    WE: 'Wednesday',
    TH: 'Thursday',
    FR: 'Friday',
    SA: 'Saturday',
  }

  const toOrdinal = (value) => {
    const n = Number(value)
    if (!n) return ''
    const v = n % 100
    if (v >= 11 && v <= 13) return `${n}th`
    switch (n % 10) {
      case 1: return `${n}st`
      case 2: return `${n}nd`
      case 3: return `${n}rd`
      default: return `${n}th`
    }
  }

  if (normalized.startsWith('FREQ=DAILY')) return 'Daily'
  if (normalized.startsWith('FREQ=WEEKLY') && normalized.includes('BYDAY=MO,TU,WE,TH,FR')) return 'Every weekday'
  if (normalized.startsWith('FREQ=WEEKLY') && byDayMatch) {
    const weekday = weekdayMap[byDayMatch[1]] || byDayMatch[1]
    return `Every ${weekday}`
  }
  if (normalized.startsWith('FREQ=MONTHLY') && byDayMatch && bySetPosMatch) {
    const weekday = weekdayMap[byDayMatch[1]] || byDayMatch[1]
    return `Monthly on the ${toOrdinal(bySetPosMatch[1])} ${weekday}`
  }
  if (normalized.startsWith('FREQ=YEARLY')) {
    if (byMonthMatch && byMonthDayMatch) {
      const month = moment(`${byMonthMatch[1]}-${byMonthDayMatch[1]}`, 'M-D').format('MMMM D')
      return `Annually on ${month}`
    }
    if (dtstartMatch) {
      const month = moment(dtstartMatch[1], 'YYYYMMDD').format('MMMM D')
      return `Annually on ${month}`
    }
    return 'Annually'
  }

  return isRecurring.value ? 'Recurring' : ''
})

const description = computed(() => {
  if (!eventInfo.value) return 'N/A'
  return eventInfo.value.notes || 'N/A'
})

const zoomLink = computed(() => {
  if (!eventInfo.value) return ''
  return eventInfo.value.external_url || eventInfo.value.meetingUrl || eventInfo.value.zoomMeetingUrl || ''
})

// Internal-only notes (VP-18049). The backend returns null for patient tokens,
// so this row simply never renders on attendee-facing views.
const internalNotes = computed(() => (eventInfo.value?.internal_notes || '').trim())

const isZoomLinkUrl = computed(() => {
  if (!zoomLink.value) return false
  // Check if it's a URL (starts with http:// or https://)
  return zoomLink.value.startsWith('http://') || zoomLink.value.startsWith('https://')
})

const reports = computed(() => {
  if (!eventInfo.value || !eventInfo.value.accession_ids || eventInfo.value.accession_ids.length === 0) return []
  
  // Generate reports from accession_ids
  const accessionIds = eventInfo.value.accession_ids
  const reportsList = []
  
  accessionIds.forEach(accessionId => {
    reportsList.push({
      id: `${accessionId}-modern`,
      name: `${accessionId} - Modern`,
      type: 'modern',
      accessionId: accessionId
    })
    reportsList.push({
      id: `${accessionId}-classic`,
      name: `${accessionId} - Classic`,
      type: 'classic',
      accessionId: accessionId
    })
  })
  
  return reportsList
})

// Group reports into pairs (2 per row) for expanded view
const groupedReports = computed(() => {
  const allReports = reports.value
  const grouped = []
  for (let i = 0; i < allReports.length; i += 2) {
    grouped.push(allReports.slice(i, i + 2))
  }
  return grouped
})

// preview position append to select time rect
const rect = computed(() => {
  return eventInfo.value?.jsEvent?.target?.getBoundingClientRect?.() || { width: 0, height: 0 };
});
const previewRef = ref(null);
const previewHeight = ref(567);
const triggerElement = ref(null);
let resizeObserver = null;

// Store the element that triggered the modal for focus return
const storeTriggerElement = () => {
  triggerElement.value = document.activeElement;
};

// Handle Esc key press
const handleKeyDown = (event) => {
  if (event.key === 'Escape' || event.key === 'Esc') {
    emit('close');
  }
};

// Cache initial position to prevent jumping when eventInfo updates from form changes
const cachedPositionX = ref(0)
const cachedPositionY = ref(0)

// Calculate position once on mount, then use cached values
const calculateInitialPosition = () => {
  if (!eventInfo.value || !eventInfo.value.jsEvent) {
    cachedPositionX.value = 0
    cachedPositionY.value = 0
    return
  }
  
  const previewWidth = 546
  const padding = 10
  const offsetX = 5
  let x = eventInfo.value.x + rect.value.width - eventInfo.value.jsEvent.offsetX + offsetX
  
  // check right boundary
  const maxX = window.innerWidth - previewWidth - padding
  if (x > maxX) {
    x = eventInfo.value.x - eventInfo.value.jsEvent.offsetX - previewWidth - offsetX
    if (x < padding) {
      x = window.innerWidth/2 - previewWidth/2
    }
  }
  
  cachedPositionX.value = x
  cachedPositionY.value = Math.min(eventInfo.value.y - 200, window.innerHeight - previewHeight.value - 140)
}

// Use cached positions to prevent recalculation
const positionx = computed(() => cachedPositionX.value)
const positiony = computed(() => cachedPositionY.value)



const expand = () => {
  // Save current form data to store before expanding
  // This ensures data entered in preview is preserved when opening detail
  const currentInfo = store.state.calendar.currentEventInfo
  const preScheduleInfo = store.state.calendar.preScheduleInfo
  
  // Merge current event info with any updates from the form
  const updatedInfo = {
    ...currentInfo,
    ...preScheduleInfo,
    event_id: preScheduleInfo?.event_id ?? currentInfo?.event_id,
    mode: currentInfo?.mode ?? preScheduleInfo?.mode,
    is_canceled: currentInfo?.is_canceled ?? preScheduleInfo?.is_canceled,
    // Preserve form field values that user may have entered
    selectedPatient: currentInfo?.selectedPatient,
    selectedProviders: currentInfo?.selectedProviders,
    selectedStartTime: currentInfo?.selectedStartTime,
    selectedEndTime: currentInfo?.selectedEndTime,
    selectedDate: currentInfo?.selectedDate,
    meetingType: currentInfo?.meetingType || preScheduleInfo?.event_type,
    location: currentInfo?.location,
  }
  
  // Update store with merged data
  store.dispatch('calendar/setCurrentEventInfo', updatedInfo)
  
  emit('expand', updatedInfo)
}

const createEncounterNotes = async () => {
  if (creatingEncounterNote.value) return

  creatingEncounterNote.value = true
  try {
    const resolvedPatientId = patientId.value
    if (!resolvedPatientId) {
      throw new Error('No required patient selected in this appointment')
    }

    const payload = {
      patient_id: resolvedPatientId,
      title: 'Initial Intake Note',
      appointment_type: appointmentType.value 
    }

    const createdNote = await createEncounterNoteApi(payload)
    const noteId = createdNote?.id
    if (!noteId) {
      throw new Error('Encounter note ID missing from response')
    }

    const token = store.state.login?.token || localStorage.getItem('token') || sessionStorage.getItem('token') || ''
    const noteUrl = token
      ? `${window.location.origin}/encounter-notes/${resolvedPatientId}/${noteId}?jwtToken=${encodeURIComponent(token)}`
      : `${window.location.origin}/encounter-notes/${resolvedPatientId}/${noteId}`
    window.open(noteUrl, '_blank', 'noopener')
  } catch (error) {
    console.error('Create encounter note failed:', error)
  } finally {
    creatingEncounterNote.value = false
  }
}

const createEvent = async () => {
  store.commit('calendar/SET_LAST_CREATED_EVENT_RESPONSE', null)
  store.commit('calendar/SET_LAST_CREATED_TASK_RESPONSE', null)
  store.commit('calendar/SET_LAST_UPDATED_TASK_RESPONSE', null)
  store.commit('calendar/SET_LAST_UPDATED_EVENT_RESPONSE', null)
  store.dispatch('calendar/setCreateOrUpdateEventAction', true)
}
watch(activeTab, (newVal) => {
  activeTab.value = newVal
  emit('update:activeTab', newVal)
}, { immediate: true })

watch(lastCreatedTaskResponse, (newVal) => {
  if (newVal && props.visible) {
    emit('close')
  }
})

watch(lastUpdatedTaskResponse, (newVal) => {
  if (newVal && props.visible) {
    emit('close')
  }
})

watch(lastUpdatedEventResponse, (newVal) => {
  if (newVal?.event_id && props.visible) {
    emit('close')
  }
})

watch(lastCreatedEventResponse, (newVal) => {
  if (newVal && props.visible) {
    emit('close')
  }
})

watch(
  () => [eventInfo.value?.event_id, eventInfo.value?.event_title, eventInfo.value?.title].join('-'),
  () => {
    reportsExpanded.value = false
  },
  { immediate: true }
)

// Recalculate position when preview opens (visible changes to true)
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    // Calculate position when preview opens
    calculateInitialPosition();
  }
}, { immediate: true })

onMounted(() => {
  // Store the trigger element for focus return
  storeTriggerElement();
  
  // Add keyboard event listener
  document.addEventListener('keydown', handleKeyDown);
  
  if (previewRef.value) {
    resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        previewHeight.value = entry.contentRect.height;
      }
    });
    resizeObserver.observe(previewRef.value);
    
    // Set focus to the modal for accessibility
    previewRef.value.focus();
  }
});

onUnmounted(() => {
  // Remove keyboard event listener
  document.removeEventListener('keydown', handleKeyDown);
  
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  
  // Return focus to trigger element
  if (triggerElement.value && triggerElement.value.focus) {
    triggerElement.value.focus();
  }
});

</script>

<style scoped>
.preview-layout {
  width: 546px;
  max-width: calc(100vw - 20px);
  background-color: #fff;
  box-shadow: 1px 1px 12px 1px #00000033;
  box-sizing: border-box;
  border-radius: 10px;
  overflow: hidden;
}

/* Internal Notes can be up to 2000 chars; cap the row at ~5 lines and scroll inside */
.internal-notes-value {
  max-height: 120px;
  overflow-y: auto;
  padding-right: 4px;
}

.encounter-note-loading-icon {
  width: 16px;
  height: 16px;
  animation: spin 1.4s linear infinite;
}
</style>